import { useState } from 'react'
import { Box, Flex, Text, VStack } from '@chakra-ui/react'
import { LuUserPlus } from 'react-icons/lu'
import { useNavigate } from 'react-router-dom'
import { DataTable, DataList, PageHeader, toaster } from '@minetrace/ui'
import { brand, themeColors } from '../../theme'
import { personsService } from '../../api/services'
import { usePagedRequest } from '../../hooks'
import type { PersonResponse } from '../../api/models/response'
import { exportToExcel, type ExcelColumn } from '../../utils/exportExcel'
import { T, useT } from '../../i18n'
import ConsentModal from '../../components/ConsentModal'
import { ApiError } from '../../api/client'

const MOBILE_ROLE_CODES = new Set(['transportista', 'geologo', 'recepcion'])

const DOC_TYPE_LABELS: Record<number, string> = {
  1: 'DNI',
  2: 'CE',
  3: 'Pasaporte',
}

const excelColumns: ExcelColumn<PersonResponse>[] = [
  { header: 'Nombre', accessor: (p) => p.fullName },
  { header: 'Documento', accessor: (p) => `${DOC_TYPE_LABELS[p.documentType] ?? 'Doc'} ${p.identityDoc}` },
  { header: 'Empresa', accessor: (p) => (p.roles.length > 0 ? p.roles[0].companyName : '') },
  { header: 'Email', accessor: (p) => p.email ?? '' },
  { header: 'Estado', accessor: (p) => (p.status === 0 ? 'Borrador' : p.userAccount ? p.userAccount.accountStatusDescription : 'Sin cuenta') },
]

export default function PersonasPage() {
  const colors = themeColors
  const navigate = useNavigate()
  const { t } = useT()

  const [search, setSearch] = useState('')
  const [showConsent, setShowConsent] = useState(false)

  const { items: personas, totalCount, isFetching: loading, error, refresh, page, setPage, totalPages, pageSize, setPageSize } = usePagedRequest(
    personsService.getAll,
    { params: { search: search.trim() || undefined } },
  )

  const getInitials = (p: PersonResponse) => {
    const first = p.names?.[0] ?? ''
    const last = p.paternalSurname?.[0] ?? ''
    return `${first}${last}`.toUpperCase()
  }

  const handleDelete = async (persona: PersonResponse) => {
    await personsService.delete(persona.id)
    refresh()
  }

  const handleResendInvitation = async (persona: PersonResponse) => {
    try {
      await personsService.resendInvitation(persona.id)
      toaster.success({ description: 'Correo de invitación reenviado con nueva contraseña temporal.' })
    } catch (e) {
      toaster.error({
        description: e instanceof ApiError ? e.message : 'No se pudo reenviar la invitación.',
      })
    }
  }

  const handleResendMobileSetup = async (persona: PersonResponse) => {
    try {
      await personsService.resendMobileSetup(persona.id)
      toaster.success({ description: 'Correo con QR de configuración móvil reenviado.' })
    } catch (e) {
      toaster.error({
        description: e instanceof ApiError ? e.message : 'No se pudo reenviar el QR.',
      })
    }
  }

  /** "Nunca ingresó" / "hace 3 días" — kept simple, no relative-time lib. */
  const formatLastLogin = (iso: string | null): string => {
    if (!iso) return 'Nunca ingresó'
    const d = new Date(iso)
    return `Último ingreso: ${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
  }

  const getRolColor = (code: string): string => {
    const colorMap: Record<string, string> = {
      superadmin: '#c8442f',
      admin: '#d49a2a',
      operador: '#3d6fa8',
      transportista: '#8B5CF6',
      proveedor: '#2a8a87',
      geologo: '#06B6D4',
      digitador: '#F97316',
    }
    return colorMap[code] ?? brand.green
  }

  return (
    <VStack gap={6} alignItems="stretch">
      <PageHeader
        title={t('personas.title')}
        subtitle={t('personas.subtitle')}
        buttonLabel={t('personas.newButton')}
        onButtonClick={() => setShowConsent(true)}
        buttonIcon={<LuUserPlus size={18} />}
        colors={colors}
      />

      <DataList<PersonResponse>
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('personas.searchPlaceholder')}
        onExport={() => exportToExcel(personas, excelColumns, 'personas')}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        entityName="personas"
        brandColor={brand.green}
        headers={[
          t('personas.headers.persona'),
          t('personas.headers.documento'),
          t('personas.headers.empresa'),
          t('personas.headers.roles'),
          t('personas.headers.cuenta'),
        ]}
        data={personas}
        loading={loading}
        error={error}
        emptyMessage={t('personas.emptyMessage')}
        loadingMessage={t('personas.loadingMessage')}
        colors={colors}
        keyExtractor={(p) => p.id}
        renderRow={(persona) => (
          <>
            {/* Avatar + Name */}
            <DataTable.Cell>
              <Flex alignItems="center" gap={3}>
                <Box
                  width="36px"
                  height="36px"
                  borderRadius="full"
                  bg={`linear-gradient(135deg, ${brand.green} 0%, ${brand.gold} 100%)`}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Text fontSize="xs" fontWeight="bold" color="white">
                    {getInitials(persona)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="500" color={colors.text} whiteSpace="nowrap">
                    {persona.fullName}
                  </Text>
                  <Text fontSize="xs" color={colors.textMuted}>
                    {persona.email ?? '-'}
                  </Text>
                </Box>
              </Flex>
            </DataTable.Cell>

            {/* Documento */}
            <DataTable.Cell>
              <Text fontSize="xs" color={colors.textMuted} mb={0.5}>
                {DOC_TYPE_LABELS[persona.documentType] ?? 'Doc'}
              </Text>
              <Text fontFamily="monospace" fontSize="sm" color={colors.text}>
                {persona.identityDoc}
              </Text>
            </DataTable.Cell>

            {/* Empresa */}
            <DataTable.Cell>
              <Text fontSize="sm" color={colors.text}>
                {persona.roles.length > 0 ? persona.roles[0].companyName : '-'}
              </Text>
            </DataTable.Cell>

            {/* Roles */}
            <DataTable.Cell>
              <Flex gap={1} flexWrap="wrap">
                {persona.roles.length > 0 ? persona.roles.map((rol) => (
                  <Box
                    key={rol.roleId}
                    display="inline-block"
                    px={2}
                    py={0.5}
                    borderRadius="md"
                    fontSize="10px"
                    fontWeight="bold"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                    bg={`${getRolColor(rol.roleCode)}20`}
                    color={getRolColor(rol.roleCode)}
                    whiteSpace="nowrap"
                  >
                    {rol.roleName}
                  </Box>
                )) : (
                  <Text fontSize="sm" color={colors.textMuted}>-</Text>
                )}
              </Flex>
            </DataTable.Cell>

            {/* Cuenta */}
            <DataTable.Cell>
              {persona.status === 0 ? (
                <Box
                  display="inline-block"
                  px={2}
                  py={0.5}
                  borderRadius="md"
                  fontSize="10px"
                  fontWeight="bold"
                  textTransform="uppercase"
                  bg="rgba(249, 115, 22, 0.2)"
                  color="#F97316"
                >
                  {t('personas.status.pending')}
                </Box>
              ) : persona.userAccount ? (
                <Box>
                  <Box
                    display="inline-block"
                    px={2}
                    py={0.5}
                    borderRadius="md"
                    fontSize="10px"
                    fontWeight="bold"
                    textTransform="uppercase"
                    bg={persona.userAccount.accountStatus === 2 ? 'rgba(42, 138, 135, 0.2)' : 'rgba(200, 68, 47, 0.2)'}
                    color={persona.userAccount.accountStatus === 2 ? '#2a8a87' : '#c8442f'}
                  >
                    {persona.userAccount.accountStatusDescription}
                  </Box>
                  <Text
                    fontSize="11px"
                    color={persona.userAccount.lastLoginAt ? colors.textMuted : '#d49a2a'}
                    fontWeight={persona.userAccount.lastLoginAt ? '400' : '600'}
                    mt={1}
                  >
                    {formatLastLogin(persona.userAccount.lastLoginAt)}
                  </Text>
                </Box>
              ) : (
                <T tid="personas.status.noAccount" fontSize="sm" color={colors.textMuted} />
              )}
            </DataTable.Cell>
          </>
        )}
        actions={(persona) => {
          if (persona.status === 0) {
            return [
              { label: t('personas.actions.continueReg'), onClick: () => navigate(`/app/personas/${persona.id}`) },
              { label: t('personas.actions.delete'), onClick: () => handleDelete(persona), color: '#c8442f' },
            ]
          }
          const acts: Array<{ label: string; onClick: () => void; color?: string }> = [
            { label: t('personas.actions.edit'), onClick: () => navigate(`/app/personas/${persona.id}`) },
          ]
          // Resend invitation only makes sense if (a) there is a user account
          // and (b) it has never been used. After first login, the user owns
          // their password and a re-invite would lock them out.
          if (persona.userAccount && !persona.userAccount.lastLoginAt) {
            acts.push({ label: 'Reenviar invitación', onClick: () => handleResendInvitation(persona) })
          }
          // Mobile QR can be resent any time — losing it (new phone, etc.)
          // is normal and the QR alone doesn't grant access.
          const hasMobileRole = persona.roles.some((r) => MOBILE_ROLE_CODES.has(r.roleCode))
          if (persona.userAccount && hasMobileRole) {
            acts.push({ label: 'Reenviar QR app móvil', onClick: () => handleResendMobileSetup(persona) })
          }
          acts.push({ label: t('personas.actions.delete'), onClick: () => handleDelete(persona), color: '#c8442f' })
          return acts
        }}
      />

      <ConsentModal
        open={showConsent}
        onClose={() => setShowConsent(false)}
        onAccept={() => {
          setShowConsent(false)
          navigate('/app/personas/nuevo')
        }}
      />
    </VStack>
  )
}
